import { $, $$, rect } from "@utils/dom";
import Drag from "@utils/drag";
import { on, off } from "@utils/listener";
import { cover, lerp, limit } from "@utils/math";
import { hover_device } from "@utils/mobile";
import ResizeOrientation from "@utils/resize";
import Viewport from "@utils/viewport";

const FPS = 1000 / 60;
const ASPECT_RATIO = 4 / 5;
const ANIMATE_IN_CLASSNAME = '--js-animate-in';
const ANIMATE_OUT_CLASSNAME = '--js-animate-out';
const TOUCH_FRICTON = 0.9;
const TOUCH_MAXIMUM_VELOCITY = 20;
const ZOOM_IN_CLASSNAME = '--js-zoom-in';
const ZOOM_CURSOR_DISABLED = '--js-cursor-disabled';

class ProductViewer {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.bg = $('.product-viewer__bg', this.el);
    this.fraction = $('.product-viewer__fraction', this.el);
    this.fractionIndex = $('.product-viewer__index', this.el);
    this.fractionTotal = $('.product-viewer__total', this.el);
    this.closeBtn = $('.product-viewer__closeBtn', this.el);
    this.nextBtn = $('.product-viewer__nextBtn', this.el);
    this.prevBtn = $('.product-viewer__prevBtn', this.el);
    this.zoomWrap = $('.product-viewer__zoomWrap', this.el);
    this.zoomBtnWrap = $('.product-viewer__zoomBtnWrap', this.el);
    this.zoomInBtn = $('.product-viewer__zoomIn', this.el);
    this.zoomOutBtn = $('.product-viewer__zoomOut', this.el);
    this.content = $('.product-viewer__content', this.el);
    this.canvas = $('.product-viewer__canvas', this.el);
    this.pagination = $('.product-viewer__pagination', this.el);
    this.triggers = [ ...$$(`[aria-controls="${this.el.id}"]`) ];
    this.layers = null;
    this.pages = null;

    this._data = {
      activeIndex: null,
      length: 0,
      canvas: { ctx: this.canvas.getContext('2d'), width: 0, height: 0, dpi: Viewport.devicePixelRatio },
      cursor: { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, lerp: 0.1 },
      panel: { x: 0, y: 0, tx: 0, ty: 0, minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 },
      target: null,
      targets: null,
      trigger: null,
    };
    this._drag = null;
    this._lastTime = null;
    this._opened = false;
    this._zooming = false;

    this._onUpdate = this._onUpdate.bind(this);
    this._onRefresh = this._onRefresh.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onOpenCompleted = this._onOpenCompleted.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onZoomClick = this._onZoomClick.bind(this);
    this._onPaginationClick = this._onPaginationClick.bind(this);
    this._onRAF = this._onRAF.bind(this);
    this._onCursorRAF = this._onCursorRAF.bind(this);
    this._onCursorEnabled = this._onCursorEnabled.bind(this);
    this._onCursorDisabled = this._onCursorDisabled.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
    this._onDragMove = this._onDragMove.bind(this);
    this._onDragEnd = this._onDragEnd.bind(this);
    this._onTouchRAF = this._onTouchRAF.bind(this);
  }

  init() {
    this.canvas.remove();

    this._drag = new Drag(this.bg, { preventScrolling: true });
    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if( this.layers ) this.layers.forEach(layer => layer.destroy());
    if( this._drag ) this._drag.destroy();

    this.el = null;
    this.emitter = null;
    this.bg = null;
    this.fraction = null;
    this.fractionIndex = null;
    this.fractionTotal = null;
    this.closeBtn = null;
    this.nextBtn = null;
    this.prevBtn = null;
    this.zoomWrap = null;
    this.zoomBtnWrap = null;
    this.zoomInBtn = null;
    this.zoomOutBtn = null;
    this.content = null;
    this.canvas = null;
    this.pagination = null;
    this.triggers = null;
    this.layers = null;
    this.pages = null;

    this._data = null;
    this._drag = null;
    this._lastTime = null;
    this._opened = null;
    this._zooming = null;

    this._onUpdate = null;
    this._onRefresh = null;
    this._onOpen = null;
    this._onOpenCompleted = null;
    this._onClose = null;
    this._onCloseCompleted = null;
    this._handleKeyDown = null;
    this._onCloseClick = null;
    this._onNextClick = null;
    this._onPrevClick = null;
    this._onZoomClick = null;
    this._onPaginationClick = null;
    this._onResize = null;
    this._onMouseMove = null;
    this._onRAF = null;
    this._onCursorRAF = null;
    this._onCursorEnabled = null;
    this._onCursorDisabled = null;
    this._onDragStart = null;
    this._onDragMove = null;
    this._onDragEnd = null;
    this._onTouchRAF = null;
  }
  start() {
    let template = $('#product-viewer__template');
    if( !template ) return;

    if( this.content ) {
      this.content.innerHTML = template.innerHTML;
      this.content.append(this.canvas);

      this.layers = [ ...$$('.product-viewer__layer', this.content) ].map(el => new Layer(el));
      this._data.length = this.layers[0].length;
      
      if( this.fractionTotal ) this.fractionTotal.textContent = this._data.length;
    }

    if( this.pagination ) {
      let template = $('#product-viewer__pagination');

      this.pagination.setAttribute('aria-hidden', template ? false : true);
      if( !template ) return;

      this.pagination.innerHTML = template.innerHTML;
      this.pages = [ ...$$('button', this.pagination) ];
      this.pagination.setAttribute('aria-hidden', this.pages.length > 1 ? false : true);
    }
  }

  _bindEvents() {
    this.emitter.on('ProductViewer.update', this._onUpdate);
    this.emitter.on('ProductViewer.refresh', this._onRefresh);
    this.emitter.on('ProductViewer.open', this._onOpen);
    this.emitter.on('ProductViewer.close', this._onClose);
    on(window, "keydown", this._handleKeyDown);

    if( this.closeBtn ) on(this.closeBtn, 'click', this._onCloseClick);
    if( this.nextBtn ) on(this.nextBtn, 'click', this._onNextClick);
    if( this.prevBtn ) on(this.prevBtn, 'click', this._onPrevClick);
    if( this.zoomWrap ) on(this.zoomWrap, 'click', this._onZoomClick);
    if( this.pagination ) on(this.pagination, 'click', this._onPaginationClick);
    if( this._drag ) {
      this._drag.on('start', this._onDragStart);
      this._drag.on('move', this._onDragMove);
      this._drag.on('end', this._onDragEnd);
    }
  }
  _unbindEvents() {
    this.emitter.off('ProductViewer.update', this._onUpdate);
    this.emitter.off('ProductViewer.refresh', this._onRefresh);
    this.emitter.off('ProductViewer.open', this._onOpen);
    this.emitter.off('ProductViewer.close', this._onClose);

    off(window, "keydown", this._handleKeyDown);
    off(window, 'mousemove', this._onMouseMove);
    ResizeOrientation.remove(this._onResize);

    if( this.bg ) off(this.bg, 'animationend', this._onCloseCompleted);
    if( this.content ) off(this.content, 'animationend', this._onOpenCompleted);
    if( this.closeBtn ) off(this.closeBtn, 'click', this._onCloseClick);
    if( this.nextBtn ) off(this.nextBtn, 'click', this._onNextClick);
    if( this.prevBtn ) off(this.prevBtn, 'click', this._onPrevClick);
    if( this.pagination ) off(this.pagination, 'click', this._onPaginationClick);
    if( this.zoomWrap ) {
      off(this.zoomWrap, 'click', this._onZoomClick);
      off(this.zoomWrap, 'mouseenter', this._onCursorEnabled);
      off(this.zoomWrap, 'mouseleave', this._onCursorDisabled);
    }
    if( this._drag ) {
      this._drag.off('start', this._onDragStart);
      this._drag.off('move', this._onDragMove);
      this._drag.off('end', this._onDragEnd);
    }
  }

  _close() {
    this.emitter.emit('ProductViewer.close', this._data.activeIndex);
  }
  _updateContentVars() {
    const { canvas, target } = this._data;

    const styles = getComputedStyle(target);
    const bcr = rect(target);
    const top = (Viewport.height - bcr.height) / 2;
    const left = (Viewport.width - bcr.width) / 2;

    // copy medias scaling and origin css variables
    const mediaScale = styles.getPropertyValue('--media-scale');
    const mediaOrigin = styles.getPropertyValue('--media-origin');

    // save canvas dimensions
    canvas.width = Math.ceil(bcr.width * mediaScale);
    canvas.height = Math.ceil(bcr.height * mediaScale);

    // resize canvas exactly like target
    this.canvas.width = canvas.width * canvas.dpi;
    this.canvas.height = canvas.height * canvas.dpi;

    this.el.style.setProperty('--media-scale', mediaScale);
    this.el.style.setProperty('--media-origin', mediaOrigin);

    // resize content exactly like target
    //this.el.style.setProperty('--content-width', `${bcr.width}px`);
    this.el.style.setProperty('--content-height', `${bcr.height}px`);

    // move content at target's position
    this.el.style.setProperty('--content-x', `${bcr.left - left}px`);
    this.el.style.setProperty('--content-y', `${bcr.top - top}px`);
  }
  _updateIndex(index) {
    if( this._data.activeIndex === index ) return;

    this._data.activeIndex = index;
    this.layers.forEach(layer => layer.index = this._data.activeIndex);

    // update fraction
    if( this.fractionIndex ) this.fractionIndex.textContent = (this._data.activeIndex + 1).toString();
  }
  _updateNavBtns() {
    const { activeIndex, length } = this._data;

    if( this.nextBtn ) this.nextBtn.disabled = activeIndex >= length - 1;
    if( this.prevBtn ) this.prevBtn.disabled = activeIndex === 0;
  }
  _updatePagination() {
    if( !this.pages ) return;

    const { activeIndex } = this._data;

    this.pages.forEach((button, index) => button.setAttribute('aria-current', index === activeIndex));
  }
  _snapZoomWrap() {
    if( !this._data.trigger/* || !hover_device*/ ) return;

    const bcr = rect(this._data.trigger);
    const { cursor } = this._data;

    cursor.x = bcr.left + bcr.width / 2;
    cursor.y = bcr.top + bcr.height / 2;
    cursor.lerp = 0.1;

    this.zoomBtnWrap.style.transform = `translate(${cursor.x}px, ${cursor.y}px)`;
  }
  _restoreZoomWrap() {
    this.zoomWrap.classList.remove(ZOOM_CURSOR_DISABLED);
    this.zoomInBtn.setAttribute('aria-hidden', this._zooming);
    this.zoomOutBtn.setAttribute('aria-hidden', !this._zooming);

    if( !this._data.trigger/* || !hover_device*/ ) return;

    const bcr = rect(this._data.trigger);
    const { cursor } = this._data;

    cursor.x = bcr.left + bcr.width / 2;
    cursor.y = bcr.top + bcr.height / 2;

    this.zoomWrap.style.setProperty('--x', `${cursor.x}px`);
    this.zoomWrap.style.setProperty('--y', `${cursor.y}px`);
  }
  _calcPanelTarget() {
    if( !hover_device ) return;

    const { cursor, panel } = this._data;
    const { x, y } = cursor;

    // percentage between [-0.5, 0.5]
    const px = limit(0, 1, x / Viewport.width) - 0.5;
    const py = limit(0, 1, y / Viewport.height) - 0.5;

    panel.tx = panel.maxX * px;
    panel.ty = panel.maxY * py;
  }
  _drawTransition(transitions) {
    const { ctx, width, height, dpi } = this._data.canvas;

    ctx.clearRect(0, 0, width * dpi, height * dpi);
    transitions.forEach(transition => {
      const ratio = transition.naturalWidth / transition.naturalHeight;
      const data = cover(width * dpi, height * dpi, ratio);

      ctx.drawImage(transition, data.x, data.y, data.width, data.height);
    });
  }
  

  _onUpdate(layers) {
    // update each layers images
    layers.forEach(l => {
      // find layer & update his images
      const layer = this.layers.find(layer => layer.type === l.type);
      if( layer ) layer.update(l.medias);
    });
  }
  _onRefresh() {
    if( this.layers ) this.layers.forEach(layer => layer.destroy());
    this.layers = null;
    this.pages = null;
    
    this.start();
  }
  _onOpen(targets, index, transitions, trigger, event, autoZoomMaxWidth = 1200) {
    if( this._opened ) return;
    this._opened = true;

    this._data.activeIndex = index;
    this._data.targets = targets;
    this._data.target = targets[index];
    this._data.trigger = trigger;

    // remove previous animations
    off(this.bg, 'animationend', this._onCloseCompleted);
    off(this.content, 'animationend', this._onOpenCompleted);
    this.el.classList.remove(ANIMATE_OUT_CLASSNAME, ANIMATE_IN_CLASSNAME);

    this._updateContentVars();
    this._updateNavBtns();
    this._updatePagination();
    this._snapZoomWrap();
    this._drawTransition(transitions);

    // show canvas
    if( this.canvas ) this.canvas.style.removeProperty('display');

    // hide layers until open transition is completed AND update active media of each layers
    if( this.layers ) {
      this.layers.forEach(layer => {
        layer.index = this._data.activeIndex;
        layer.hide();
      });
    }

    // update fraction
    if( this.fractionIndex ) this.fractionIndex.textContent = (this._data.activeIndex + 1).toString();

    // notify every trigger buttons
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', true));

    // show modal & animate in
    on(this.content, 'animationend', this._onOpenCompleted);
    this.el.setAttribute('aria-hidden', false);
    this.el.classList.add(ANIMATE_IN_CLASSNAME);

    // for small viewports, zoom immediately
    if( Viewport.width < autoZoomMaxWidth ) this._onZoomClick();

    if( hover_device ) {
      this._data.cursor.tx = event.clientX;
      this._data.cursor.ty = event.clientY;

      on(window, 'mousemove', this._onMouseMove);
      on(this.zoomWrap, 'mouseenter', this._onCursorEnabled);
      on(this.zoomWrap, 'mouseleave', this._onCursorDisabled);

      this._onCursorRAF();
    }
  }
  _onOpenCompleted(event) {
    if( event.target !== this.content ) return;

    off(this.content, 'animationend', this._onOpenCompleted);

    // show all layers
    if( this.layers ) this.layers.forEach(layer => layer.show());

    // hide canvas
    if( this.canvas ) this.canvas.style.display = 'none';
  }
  _onClose() {
    if( !this._opened ) return;

    this._opened = false;
    this._zooming = false;

    ResizeOrientation.remove(this._onResize);
    off(window, 'mousemove', this._onMouseMove);
    off(this.zoomWrap, 'mouseenter', this._onCursorEnabled);
    off(this.zoomWrap, 'mouseleave', this._onCursorDisabled);
    off(this.bg, 'animationend', this._onCloseCompleted);
    off(this.content, 'animationend', this._onOpenCompleted);

    this._drag.stop();

    // update destination target
    const { activeIndex, targets } = this._data;
    this._data.target = targets[activeIndex];

    this._updateContentVars();
    this._restoreZoomWrap();

    // show all layers
    if( this.layers ) this.layers.forEach(layer => layer.show());

    // hide canvas
    if( this.canvas ) this.canvas.style.display = 'none';

    // notify every trigger buttons
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', false));

    // animate out
    on(this.bg, 'animationend', this._onCloseCompleted);
    this.el.classList.add(ANIMATE_OUT_CLASSNAME);
  }
  _onCloseCompleted() {
    off(this.bg, 'animationend', this._onCloseCompleted);

    // hide modal
    this.el.setAttribute('aria-hidden', true);

    // remove zoom-in
    this.el.classList.remove(ZOOM_IN_CLASSNAME);

    // dispatch event
    this.emitter.emit('ProductViewer.closed', this._data.activeIndex);

    // reset all layers
    if( this.layers ) this.layers.forEach(layer => layer.reset());
  }

  _handleKeyDown(event) {
    if( !this._opened ) return;

    const { key } = event;

    if( key === "Escape" || key === "Esc" ) this._close();
    else if( key === "ArrowRight" || key === "ArrowLeft" ) {
      if( this._zooming ) return;

      if( key === "ArrowRight" ) this._onNextClick();
      else this._onPrevClick();
    }
  }
  _onCloseClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this._close();
  }
  _onNextClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    const { activeIndex, length } = this._data;

    let index = activeIndex + 1;

    // prevent looping
    if( index >= length ) return;

    // loop
    //if( index >= length ) index = 0;

    this._updateIndex(index);
    this._updateNavBtns();
    this._updatePagination();
  }
  _onPrevClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    const { activeIndex, length } = this._data;

    let index = activeIndex - 1;

    // prevent looping
    if( index < 0 ) return;

    // loop
    //if( index < 0 ) index = length - 1;

    this._updateIndex(index);
    this._updateNavBtns();
    this._updatePagination();
  }
  _onZoomClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this._zooming = !this._zooming;

    if( this._zooming ) {
      const { panel } = this._data;

      panel.x = 0;
      panel.y = 0;

      this._onResize();
      ResizeOrientation.add(this._onResize);
    } else {
      ResizeOrientation.remove(this._onResize);
      this._lastTime = null;

      //const { cursor } = this._data;

      //cursor.vx = cursor.vy = 0;
      //cursor.x = cursor.y = 0;
    }


    if( hover_device ) {
      this.zoomInBtn.setAttribute('aria-hidden', this._zooming);
      this.zoomOutBtn.setAttribute('aria-hidden', !this._zooming);
    }
    else this._onCursorDisabled();

    this.el.classList[this._zooming ? 'add' : 'remove'](ZOOM_IN_CLASSNAME);

    if( this._zooming && hover_device ) this._onRAF();
    else if( !hover_device ) {
      if( this._zooming ) {
        this._drag.start();
        this._onTouchRAF();
      }
      else this._drag.stop();
    }
  }
  _onPaginationClick(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if( !this.pages ) return;

    const target = event.target;
    const index = this.pages.findIndex(page => page === target);

    if( index < 0 ) return;

    this._updateIndex(index);
    this._updateNavBtns();
    this._updatePagination();
  }

  _onResize() {
    if( !this._zooming ) return;

    const { panel } = this._data;

    if( Viewport.width < 1200 ) {
      // if panel fit viewport's height
      panel.height = Viewport.height;
      panel.width = panel.height * ASPECT_RATIO;

      // if panel fit viewport's width
      //panel.width = Viewport.width * 2;
      //panel.height = panel.width / ASPECT_RATIO;
    } else {
      panel.width = Viewport.width;
      panel.height = panel.width / ASPECT_RATIO;
    }

    if( hover_device ) {
      panel.minX = 0;
      panel.minY = 0;
      panel.maxX = Math.min(0, Viewport.width - panel.width);
      panel.maxY = Math.min(0, Viewport.height - panel.height);
    } else {
      const w = Math.min(0, (Viewport.width - panel.width) * 0.5);
      const h = Math.min(0, (Viewport.height - panel.height) * 0.5);

      panel.minX = w;
      panel.minY = h;
      panel.maxX = w * -1;
      panel.maxY = h * -1;
    }

    this._calcPanelTarget();
  }
  _onMouseMove(event) {
    this._data.cursor.tx = event.clientX;
    this._data.cursor.ty = event.clientY;

    if( this._zooming ) this._calcPanelTarget();
  }
  _onCursorRAF() {
    if( !this._opened ) return;

    const { cursor } = this._data;

    cursor.x = lerp(cursor.x, cursor.tx, cursor.lerp);
    cursor.y = lerp(cursor.y, cursor.ty, cursor.lerp);

    if( cursor.lerp < 0.8 ) cursor.lerp = lerp(cursor.lerp, 1, 0.01);
    else cursor.lerp = 1; 

    this.zoomBtnWrap.style.transform = `translate(${cursor.x}px, ${cursor.y}px)`;

    requestAnimationFrame(this._onCursorRAF);
  }
  _onRAF() {
    if( !this._zooming ) return;

    const time = performance.now();
    const delta = this._lastTime ? Math.min(Math.ceil((time - this._lastTime) / FPS * 10) / 10, 1) : 1;

    this._lastTime = time;

    const { panel } = this._data;

    panel.x = lerp(panel.x, panel.tx, 0.12 * delta);
    panel.y = lerp(panel.y, panel.ty, 0.12 * delta);

    if( this.layers ) this.layers.forEach(layer => layer.move(panel.x, panel.y));

    requestAnimationFrame(this._onRAF);
  }
  _onCursorEnabled() { this.zoomWrap.classList.remove(ZOOM_CURSOR_DISABLED); }
  _onCursorDisabled() { this.zoomWrap.classList.add(ZOOM_CURSOR_DISABLED); }

  _onDragStart(event) {
    const { cursor } = this._data;

    cursor.vx = cursor.vy = 0;
    cursor.x = event.offsetX;
    cursor.y = event.offsetY;
  }
  _onDragMove(event) {
    const { cursor } = this._data;
    const x = event.offsetX;
    const y = event.offsetY;

    // get distance from previous cursor x/y
    cursor.vx = limit(TOUCH_MAXIMUM_VELOCITY * -1, TOUCH_MAXIMUM_VELOCITY, cursor.vx + (x - cursor.x) * 0.25);
    cursor.vy = limit(TOUCH_MAXIMUM_VELOCITY * -1, TOUCH_MAXIMUM_VELOCITY, cursor.vy + (y - cursor.y) * 0.25);

    // save cursor x/y
    cursor.x = x;
    cursor.y = y;
  }
  _onDragEnd(event) {
    const { cursor } = this._data;

    if( event ) {
      // save cursor x/y
      cursor.x = event.offsetX;
      cursor.y = event.offsetY;
    }
  }
  _onTouchRAF() {
    if( !this._zooming ) return;

    const { cursor, panel } = this._data;

    // add friction
    cursor.vx *= TOUCH_FRICTON;
    cursor.vy *= TOUCH_FRICTON;

    // move panel
    panel.x = limit(panel.minX, panel.maxX, panel.x + cursor.vx);
    panel.y = limit(panel.minY, panel.maxY, panel.y + cursor.vy);

    if( this.layers ) this.layers.forEach(layer => layer.move(panel.x, panel.y));
    requestAnimationFrame(this._onTouchRAF);
  }
}

class Layer {
  constructor(el) {
    this.el = el;
    this.medias = [ ...$$('.product-viewer__media', this.el) ].map(el => new Media(el));

    this._index = 0;
    this._type = this.el.dataset.type;
  }

  destroy() {
    if( this.medias ) this.medias.forEach(media => media.destroy());

    this.el = null;
    this.medias = null;

    this._index = null;
    this._type = null;
  }
  show() {
    this.el.setAttribute('aria-hidden', false);
  }
  hide() {
    this.el.setAttribute('aria-hidden', true);
  }
  move(x, y) {
    this.medias[this._index].panel.style.transform = `translate(${x}px, ${y}px)`;
  }
  reset() {
    this.medias?.forEach(media => {
      media.hide();
      media.reset();
    });
  }
  update(medias) {
    // update each medias
    this.medias.forEach(media => media.update(medias ? medias[media.side] : null));
  }


  // getter - setter
  get index() { return this._index; }
  set index(value) {
    // hide previous media
    this.medias[this._index].hide();

    // update index
    this._index = value;

    // show current media
    this.medias[this._index].show();
  }

  get length() { return this.medias.length; }
  get type() { return this._type; }
}

class Media {
  constructor(el) {
    this.el = el;
    this.panel = $('.product-viewer__panel', this.el);
    this.img = $('.product-viewer__img', this.el);

    this._side = this.el.dataset.side;
  }

  destroy() {
    this.el = null;
    this.panel = null;
    this.img = null;

    this._side = null;
  }
  reset() {
    this.panel.style.removeProperty('transform');
  }
  update(media) {
    if( !this.img ) return;

    if( media ) {
      this.img.src = media.src;
      this.img.srcset = media.srcset;
      this.img.sizes = media.sizes;
      this.img.width = media.width;
      this.img.height = media.height;
      this.img.removeAttribute('aria-hidden');
    } else {
      this.img.setAttribute('aria-hidden', true);
    }
  }

  show() { this.el.setAttribute('aria-hidden', false); }
  hide() { this.el.setAttribute('aria-hidden', true); }


  // getter - setter
  get side() { return this._side; }
}

export default ProductViewer;
